import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Presentation, X, ArrowUpRight, LogOut } from "lucide-react";

const steps = [
  { n: "01", label: "Homepage", to: "/", note: "Cinematic flagship experience" },
  { n: "02", label: "Menu Experience", to: "/menu", note: "Interactive editorial menu" },
  { n: "03", label: "Private Dining", to: "/private-dining", note: "Revenue-driving events" },
  { n: "04", label: "Mobile Experience", to: "/mobile", note: "Mobile-first showcase" },
  { n: "05", label: "Multi-Brand Vision", to: "/vision", note: "The bigger picture" },
  { n: "06", label: "IA Site Audit", to: "/site-audit", note: "Current → proposed architecture" },
];

export const PresentationMode = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="presentation-mode-toggle"
        className="fixed bottom-24 lg:bottom-5 left-5 z-50 flex items-center gap-2.5 bg-[#161412] border border-[#C1272D]/40 text-[#C1272D] px-4 py-3 text-[10px] font-semibold tracking-[0.2em] uppercase hover:bg-[#C1272D] hover:text-[#0a0908] transition-colors duration-400 shadow-2xl"
      >
        <Presentation size={15} />
        <span className="hidden sm:inline">Concept Presentation</span>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
            />
            <motion.div
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="fixed left-0 top-0 bottom-0 z-50 w-full max-w-md bg-[#0a0908] border-r border-white/10 overflow-y-auto"
              data-testid="presentation-panel"
            >
              <div className="p-8 md:p-10">
                <div className="flex items-start justify-between mb-10">
                  <div>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#C1272D]">Concept Presentation</p>
                    <h2 className="font-serif text-4xl text-white mt-3 leading-none">Guided Tour</h2>
                    <p className="text-white/50 text-sm font-light mt-3">Jump directly to any part of the concept.</p>
                  </div>
                  <button onClick={() => setOpen(false)} data-testid="presentation-close" className="text-white/60 hover:text-white">
                    <X size={22} />
                  </button>
                </div>

                <div className="space-y-1">
                  {steps.map((s) => {
                    const active = location.pathname === s.to;
                    return (
                      <Link
                        key={s.n}
                        to={s.to}
                        onClick={() => setOpen(false)}
                        data-testid={`presentation-step-${s.n}`}
                        className={`group flex items-center gap-5 py-5 px-4 border-b border-white/10 transition-colors ${
                          active ? "bg-[#C1272D]/10" : "hover:bg-white/5"
                        }`}
                      >
                        <span className="font-serif text-2xl text-[#C1272D] w-8">{s.n}</span>
                        <div className="flex-1">
                          <span className="block text-white text-sm tracking-[0.15em] uppercase">{s.label}</span>
                          <span className="block text-white/40 text-xs font-light mt-1">{s.note}</span>
                        </div>
                        <ArrowUpRight size={18} className="text-white/30 group-hover:text-[#C1272D] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                      </Link>
                    );
                  })}
                </div>

                <div className="mt-10 p-6 border border-[#C1272D]/20 bg-[#161412]">
                  <p className="font-serif text-2xl text-white leading-tight">One hospitality family.</p>
                  <p className="text-white/50 text-sm font-light mt-2">
                    River Stone is the flagship of a scalable multi-brand system built for Pack Brothers Hospitality.
                  </p>
                </div>

                <button onClick={() => { setOpen(false); navigate("/"); }} data-testid="presentation-exit"
                  className="mt-6 w-full flex items-center justify-center gap-2 border border-white/15 text-white/60 hover:text-white hover:border-white/40 py-3 text-[10px] font-semibold tracking-[0.25em] uppercase transition-colors">
                  <LogOut size={14} /> Exit Presentation Mode
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
